import { FC, ReactNode, useMemo } from 'react';
import { z } from 'zod';
import { CatalogEntryIARecommendation } from '@/components/content/CatalogEntryIARecommendation';
import { ProductType } from '@/data/types/Product';
import type {
	DpIARecommendationSizeMode,
	DpIARecommendationSizes,
	DpIARecommendationType,
	IARecommendationSource,
} from '@/data/Content/CatalogEntryIARecommendation';
import { CLICK_PRODUCT_VIEW_SOURCE } from '@/data/constants/clickProductView';
import {
	allOutfitPartNumbers,
	partNumbersIARecommendationExtractorByUnknown,
	selectMostAvailableOutfit,
} from '@/utils/partNumbersIARecommendationExtractorByUnknown';

const paramsSchema = z.object({
	partNumber: z.string(),
});

const transformSchema = z
	.object({
		order: z.enum([
			'similarity',
			'ascending-price',
			'descending-price',
			'ascending-offer',
			'descending-offer',
		]),
	})
	.optional();

const getParamsFromUnknown = (rawParams: unknown) => {
	try {
		const parsed = paramsSchema.safeParse(rawParams);
		if (!parsed.success) {
			return null;
		}

		return parsed.data;
	} catch (error) {
		console.error(error);
		return null;
	}
};

const getTransformFromUnknown = (rawTransform: unknown) => {
	try {
		const parsed = transformSchema.safeParse(rawTransform);

		if (!parsed.success) {
			return null;
		}

		return parsed.data;
	} catch (error) {
		console.error(error);
		return null;
	}
};

const filtersSchema = z
	.array(
		z.object({
			name: z.string(),
			operation: z.union([z.string(), z.record(z.unknown())]),
		})
	)
	.optional();

const getFiltersFromUnknown = (rawFilters: unknown) => {
	try {
		const parsed = filtersSchema.safeParse(rawFilters);
		if (!parsed.success) {
			return null;
		}
		return parsed.data ?? [];
	} catch (error) {
		console.error(error);
		return null;
	}
};

export type IACarouselType = 'recommendation' | 'outfit' | 'complements' | 'sameBrand' | 'family';

const MIN_OUTFIT_PRODUCTS = 4;

const emptyExtractor = (
	products: ProductType[],
	type: IACarouselType,
	minProducts = type === 'outfit' ? MIN_OUTFIT_PRODUCTS : 1,
	excludedPartNumber?: string
) => products.filter(({ partNumber }) => partNumber !== excludedPartNumber).length < minProducts;

export const DpCarouselIARecommendation: FC<{
	id: string;
	title?: string;
	params: unknown;
	transform: unknown;
	filters: unknown;
	type?: IACarouselType;
	showRecommendationBadge?: boolean;
	highlightFirstProduct?: boolean;
	clickSource?: CLICK_PRODUCT_VIEW_SOURCE;
	types?: DpIARecommendationType[];
	sizes?: DpIARecommendationSizes;
	sizeMode?: DpIARecommendationSizeMode;
	header?: ReactNode;
	highlightLabel?: string;
	minProducts?: number;
	excludeFromCount?: string;
	pickMostAvailableOutfit?: boolean;
	source?: IARecommendationSource;
	seedPartNumbers?: string[];
	withoutIdentity?: boolean;
	productsSelector?: (products: ProductType[]) => ProductType[];
}> = ({
	id,
	title,
	params: rawParams,
	transform: rawTransform,
	type = 'recommendation',
	highlightFirstProduct,
	showRecommendationBadge,
	clickSource,
	filters: rawFilters,
	types,
	sizes,
	sizeMode,
	header,
	highlightLabel,
	minProducts,
	excludeFromCount,
	pickMostAvailableOutfit,
	source,
	seedPartNumbers,
	withoutIdentity,
	productsSelector,
}) => {
	const params = useMemo(() => {
		const params = getParamsFromUnknown(rawParams);
		return params;
	}, [rawParams]);

	const transform = useMemo(() => {
		const transform = getTransformFromUnknown(rawTransform);
		return transform;
	}, [rawParams]);

	const filters = useMemo(() => {
		const baseFilters = getFiltersFromUnknown(rawFilters) ?? [];
		return baseFilters;
	}, [rawFilters, params]);

	if (!params) {
		return null;
	}

	const mostAvailableOutfit = type === 'outfit' && !!pickMostAvailableOutfit;

	return (
		<CatalogEntryIARecommendation
			id={id}
			title={title}
			params={params}
			transform={transform}
			highlightFirstProduct={highlightFirstProduct}
			showRecommendationBadge={showRecommendationBadge}
			clickSource={clickSource}
			filters={filters}
			types={types}
			sizes={sizes}
			sizeMode={sizeMode}
			header={header}
			highlightLabel={highlightLabel}
			source={source}
			seedPartNumbers={seedPartNumbers}
			withoutIdentity={withoutIdentity}
			partNumbersExtractor={(data) =>
				mostAvailableOutfit
					? allOutfitPartNumbers(data)
					: partNumbersIARecommendationExtractorByUnknown(type, data)
			}
			productsSelector={mostAvailableOutfit ? selectMostAvailableOutfit : productsSelector}
			emptyExtractor={(data) => emptyExtractor(data, type, minProducts, excludeFromCount)}
		/>
	);
};
